import { Link } from '@tanstack/react-router';

import { ChevronLeft } from 'lucide-react';

import markUrl from '@src/assets/autoorbit-mark.svg';
import { cn, ThemeToggle } from '@src/components/ui';


const STEP_COUNT = 3;


/** Linear 3-segment progress bar for the mobile onboarding chrome (M Create Org
 * / M Onboarding Location frames): "Step N of 3" + 3 bars, completed bars filled
 * with the brand gradient. */
function MobileStepper({ step }) {
  return (
    <div className="flex flex-col gap-[9px]">
      <span className="text-[12px] font-medium text-ink-subtle">
        {`Step ${step + 1} of ${STEP_COUNT}`}
      </span>

      <div className="flex gap-1.5">
        {Array.from({ length: STEP_COUNT }, (unused, index) => (
          <span
            key={index}
            className={cn(
              'h-1 flex-1 rounded-full',
              index <= step ? 'bg-brand-gradient' : 'bg-surface-2',
            )}
          />
        ))}
      </div>
    </div>
  );
}

MobileStepper.displayName = 'MobileStepper';


/** Onboarding step scaffold. Desktop (lg:) per the .pen "Onboarding N" frames:
 * canvas + radial glow, centered column with brand, 3-dot stepper (active =
 * gradient pill), centered heading + sub. Mobile (max-lg:) per the "M Create Org"
 * / "M Onboarding Location" frames: 56px AppBar (chevron-left + AutoOrbit +
 * hairline), left-aligned top-anchored form, a 3-segment linear progress bar
 * instead of dots, and a 22px heading. */
function OnboardingShell({ step = 0, title, sub, width = 'max-w-[440px]', backTo, children }) {
  return (
    <div className="relative flex min-h-full flex-col bg-canvas lg:items-center lg:justify-center lg:p-6">
      <ThemeToggle className="absolute right-3 top-3 z-10" />

      {/* Mobile AppBar (M frames): chevron-left + wordmark + bottom hairline. */}
      <div className="flex h-14 shrink-0 items-center gap-2.5 border-b border-hairline bg-surface-1 px-3.5 lg:hidden">
        {backTo
          ? (
            <Link aria-label="Back" to={backTo}>
              <ChevronLeft className="size-[22px] text-ink" />
            </Link>
          )
          : null}

        <span className="font-semibold text-ink">AutoOrbit</span>
      </div>

      <div
        className={cn(
          'pointer-events-none absolute inset-x-0 top-0 hidden h-[60%] lg:block',
          'bg-[radial-gradient(ellipse_45%_30%_at_50%_16%,rgba(99,102,241,0.18),transparent)]',
        )}
      />

      <div
        className={cn(
          'relative flex w-full flex-1 flex-col items-start gap-5 p-6 text-left',
          'lg:flex-none lg:items-center lg:gap-6 lg:p-0 lg:text-center',
          width,
        )}
      >
        {/* Brand + dot stepper: desktop only. */}
        <div className="hidden items-center gap-2.5 lg:flex">
          <img alt="" src={markUrl} aria-hidden="true" className="size-[30px] rounded-[9px]" />
          <span className="text-lg font-semibold text-ink">AutoOrbit</span>
        </div>

        <div className="hidden items-center gap-1.5 lg:flex">
          {[0, 1, 2].map((dot) => (
            <span
              key={dot}
              className={cn(
                'h-[7px] rounded-full',
                dot === step ? 'bg-primary w-[22px]' : 'w-[7px] bg-hairline-strong',
              )}
            />
          ))}
        </div>

        {/* Linear segment progress bar: mobile only. */}
        <div className="w-full lg:hidden">
          <MobileStepper step={step} />
        </div>

        <div className="flex w-full flex-col gap-2">
          <h1 className="text-[22px] font-semibold tracking-[-0.7px] text-ink lg:text-[26px]">{title}</h1>
          <p className="text-[15px] leading-[1.45] text-ink-muted">{sub}</p>
        </div>

        {children}
      </div>
    </div>
  );
}

OnboardingShell.displayName = 'OnboardingShell';
export default OnboardingShell;
